/**
 * Procedural sound, kept from the prototype's best idea: every composite step raises the
 * pitch a little (tension) and landing on a prime resolves it with a chord.
 *
 * Everything is synthesised with WebAudio, so there are no audio files to load. The context
 * is created lazily on the first tap because browsers only allow sound after a gesture.
 */

type Wave = OscillatorType;

interface Voice {
  freq: number;
  when?: number;
  type?: Wave;
  gain?: number;
  attack?: number;
  decay?: number;
}

const A4 = 440;
const hz = (semitonesFromA4: number) => A4 * 2 ** (semitonesFromA4 / 12);

/** Major-pentatonic climb used for the tension ticks (in semitones from A3). */
const TENSION_STEPS = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24, 26, 28, 31];

/** Rotating prime voicings so a long walk doesn't repeat one chord. Semitones from A4. */
const PRIME_CHORDS = [
  [-7, -3, 0, 5], // D major add9 colour
  [-12, -5, 0, 4, 7], // A major, open
  [-2, 2, 5, 10], // G-ish suspended
  [-9, -5, -2, 3], // C lydian shimmer
];

class Sound {
  #ctx: AudioContext | null = null;
  #master: GainNode | null = null;
  #tension = 0;
  #chord = 0;
  enabled = true;
  volume = 0.7;

  /** Must be called from inside a user gesture at least once. */
  unlock(): void {
    if (!this.enabled) return;
    if (!this.#ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -18;
      comp.ratio.value = 4;
      const master = ctx.createGain();
      master.connect(comp);
      comp.connect(ctx.destination);
      this.#ctx = ctx;
      this.#master = master;
    }
    if (this.#ctx.state === 'suspended') void this.#ctx.resume();
    this.#master!.gain.value = this.volume * 0.55;
  }

  setVolume(v: number): void {
    this.volume = v;
    if (this.#master) this.#master.gain.value = v * 0.55;
  }

  #voice({ freq, when = 0, type = 'sine', gain = 0.1, attack = 0.004, decay = 0.4 }: Voice): void {
    const ctx = this.#ctx;
    if (!ctx || !this.#master) return;
    const t = ctx.currentTime + when;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(gain, t + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    osc.connect(env);
    env.connect(this.#master);
    osc.start(t);
    osc.stop(t + attack + decay + 0.02);
  }

  #ready(): boolean {
    return this.enabled && this.#ctx !== null && this.#ctx.state === 'running';
  }

  /** A composite step: a short pluck that climbs with each step since the last prime. */
  tick(): void {
    if (!this.#ready()) return;
    const step = TENSION_STEPS[Math.min(this.#tension, TENSION_STEPS.length - 1)]!;
    this.#tension++;
    const f = hz(step - 12);
    this.#voice({ freq: f, type: 'triangle', gain: 0.05, decay: 0.07 });
  }

  /** Landing on a prime: an arpeggiated chord that releases the built-up tension. */
  prime(quiet = false): void {
    if (!this.#ready()) return;
    const chord = PRIME_CHORDS[this.#chord++ % PRIME_CHORDS.length]!;
    const g = quiet ? 0.035 : 0.075;
    // Higher tension → brighter resolution.
    const lift = Math.min(this.#tension, 8) > 4 ? 12 : 0;
    chord.forEach((st, i) =>
      this.#voice({ freq: hz(st + lift - 12), when: i * 0.024, gain: g, decay: 0.7 }),
    );
    this.#voice({ freq: hz(chord[0]! - 24), type: 'sine', gain: g * 1.2, decay: 0.35 });
    this.#tension = 0;
  }

  /** Completing a twin pair: two quick bright chords a whole step apart. */
  twin(): void {
    if (!this.#ready()) return;
    [0, 7, 12, 16].forEach((st, i) =>
      this.#voice({ freq: hz(st), type: 'triangle', when: i * 0.03, gain: 0.06, decay: 0.5 }),
    );
    [2, 9, 14, 18].forEach((st, i) =>
      this.#voice({
        freq: hz(st),
        type: 'triangle',
        when: 0.14 + i * 0.03,
        gain: 0.06,
        decay: 0.8,
      }),
    );
    this.#tension = 0;
  }

  /** A famous number: a small bell (inharmonic partial gives the metallic ring). */
  famous(): void {
    if (!this.#ready()) return;
    const f = hz(7);
    this.#voice({ freq: f, gain: 0.08, decay: 1.2 });
    this.#voice({ freq: f * 2.76, gain: 0.03, decay: 0.6 });
    this.#voice({ freq: f * 5.4, gain: 0.012, decay: 0.3 });
  }

  /** Bumping into the horizon. */
  blocked(): void {
    if (!this.#ready()) return;
    this.#voice({ freq: 98, type: 'sine', gain: 0.12, decay: 0.18 });
    this.#voice({ freq: 73, type: 'triangle', gain: 0.05, when: 0.05, decay: 0.2 });
  }

  purchase(): void {
    if (!this.#ready()) return;
    [0, 4, 7, 12].forEach((st, i) =>
      this.#voice({ freq: hz(st), when: i * 0.05, gain: 0.06, decay: 0.35 }),
    );
  }

  trialEnd(best: boolean): void {
    if (!this.#ready()) return;
    const notes = best ? [0, 4, 7, 12, 16, 19] : [0, 7, 12];
    notes.forEach((st, i) =>
      this.#voice({ freq: hz(st), type: 'triangle', when: i * 0.07, gain: 0.06, decay: 0.6 }),
    );
  }
}

export const sound = new Sound();
